/**
 * Reconciliation Review Panel — Task 7
 * ======================================
 *
 * Merchant-reconciliation override UI. Reads the override-applied
 * reconciliation view from GET /api/v1/reconciliation/override/{id}
 * and lets the accountant repair ACH matches via four actions:
 *
 *   Unmatch         — flip a wrong match back to "unmatched"
 *   Rematch         — attach a different bank deposit to a payout
 *   Correct Amount  — fix a stale matched_bank_amount
 *   Skip Platform   — exclude all matches from a given platform
 *
 * Every write posts to PATCH /api/v1/reconciliation/override/{id} and
 * appends to a hash-chained audit trail server-side (one entry per
 * action, per the default decision on audit granularity).
 *
 * Raw reconciliation data is never mutated on disk — overrides sit in
 * a sibling `overrides[]` subdocument and are applied at read time,
 * so the audit history renders the full override lineage.
 */
import React, { useEffect, useState } from 'react';
import {
  Loader2,
  XCircle,
  RefreshCw,
  DollarSign,
  SkipForward,
  Clock,
  AlertCircle,
} from 'lucide-react';

import api from '../../utils/api';
import { FinancialValue } from '../shared/FinancialValue';

// ---------------------------------------------------------------------------
// API types — mirror the backend ReconciliationSummary.to_dict()
// ---------------------------------------------------------------------------

interface AchMatch {
  ach_date: string;
  ach_net: number;
  ach_platform: string;
  matched_bank_date: string | null;
  matched_bank_amount: number | null;
  matched_bank_description: string | null;
  amount_delta: number | null;
  is_match: boolean;
}

interface YearReconciliation {
  year: number;
  platforms_detected: string[];
  ach_matched_count: number;
  ach_unmatched_count: number;
  ach_matches: AchMatch[];
}

interface ReconciliationView {
  claim_id: string;
  platforms_detected_all_years: string[];
  ach_matched_total: number;
  ach_unmatched_total: number;
  ach_match_rate_pct: number | null;
  years: YearReconciliation[];
}

interface OverrideRecord {
  override_id: string;
  action: string;
  target: Record<string, any>;
  new_value: Record<string, any>;
  reason: string;
  author_name: string;
  created_at: string;
}

interface ListResponse {
  report_id: string;
  overrides: OverrideRecord[];
  reconciliation: ReconciliationView | null;
}

type ActionKind = 'unmatch' | 'rematch' | 'correct_amount' | 'skip_platform';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
  reportId: string;
}

export default function ReconciliationReviewPanel({ reportId }: Props) {
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [modalState, setModalState] = useState<{
    kind: ActionKind;
    match?: AchMatch;
    year?: number;
    platform?: string;
  } | null>(null);

  async function reload() {
    setLoading(true);
    setLoadError(null);
    try {
      const resp = await api.get<ListResponse>(
        `/api/v1/reconciliation/override/${reportId}`,
      );
      setData(resp);
    } catch (err: any) {
      setLoadError(err?.message || 'Failed to load reconciliation.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  async function submitOverride(payload: {
    action: ActionKind;
    target: Record<string, any>;
    new_value: Record<string, any>;
    reason: string;
  }) {
    setSubmitting(true);
    setActionError(null);
    try {
      await api.patch(`/api/v1/reconciliation/override/${reportId}`, payload);
      await reload();
      setModalState(null);
    } catch (err: any) {
      setActionError(err?.message || 'Override failed.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading && !data) {
    return (
      <div className="p-6 flex items-center gap-2" style={{ color: 'var(--color-text-muted)' }}>
        <Loader2 size={16} className="animate-spin" />
        Loading reconciliation…
      </div>
    );
  }

  if (loadError) {
    return (
      <div
        role="alert"
        className="m-6 rounded-lg border p-4"
        style={{ borderColor: 'var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
      >
        <AlertCircle className="inline mr-2" size={18} />
        {loadError}
      </div>
    );
  }

  if (!data?.reconciliation) {
    return (
      <div className="p-6 text-sm" style={{ color: 'var(--color-text-muted)' }}>
        No merchant reconciliation available for this case yet. Upload
        merchant statements (Stripe, Square, Toast, etc.) to generate
        one.
      </div>
    );
  }

  const r = data.reconciliation;

  return (
    <div className="p-6 space-y-6" data-testid="reconciliation-review-panel">
      {/* Header stats */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Merchant Reconciliation Review
          </h2>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
            {r.ach_matched_total} matched · {r.ach_unmatched_total} unmatched
            {r.ach_match_rate_pct !== null && (
              <>
                {' '}· <span className="font-mono">{r.ach_match_rate_pct}%</span> match rate
              </>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {r.platforms_detected_all_years.map((p) => (
            <button
              key={p}
              onClick={() =>
                setModalState({ kind: 'skip_platform', platform: p })
              }
              className="px-2.5 py-1 rounded-full text-xs transition-colors"
              style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
              title={`Skip all ${p} matches`}
              data-testid={`skip-platform-${p}`}
            >
              <SkipForward size={11} className="inline mr-1" />
              {p}
            </button>
          ))}
        </div>
      </div>

      {actionError && (
        <div
          role="alert"
          data-testid="action-error"
          className="rounded-md border p-3 text-sm"
          style={{ borderColor: 'var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
        >
          {actionError}
        </div>
      )}

      {/* Per-year match tables */}
      {r.years.map((y) => (
        <YearSection
          key={y.year}
          year={y}
          onAction={(kind, match) => setModalState({ kind, match, year: y.year })}
        />
      ))}

      {/* Override history */}
      <OverrideHistory overrides={data.overrides} />

      {/* Action modal */}
      {modalState && (
        <OverrideModal
          state={modalState}
          submitting={submitting}
          onSubmit={submitOverride}
          onCancel={() => {
            setModalState(null);
            setActionError(null);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface YearSectionProps {
  year: YearReconciliation;
  onAction: (kind: ActionKind, match: AchMatch) => void;
}

function YearSection({ year, onAction }: YearSectionProps) {
  return (
    <div className="border rounded-lg overflow-hidden" style={{ borderColor: 'var(--color-border)' }}>
      <div className="px-4 py-2 flex items-center justify-between" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
        <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{year.year}</h3>
        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
          {year.ach_matched_count} / {year.ach_matched_count + year.ach_unmatched_count}
        </span>
      </div>

      {year.ach_matches.length === 0 ? (
        <p className="px-4 py-6 text-sm" style={{ color: 'var(--color-text-dim)' }}>
          No ACH payouts in this year.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs border-b" style={{ color: 'var(--color-text-muted)', borderColor: 'var(--color-border)' }}>
              <th className="px-4 py-2 font-medium">ACH Date</th>
              <th className="px-4 py-2 font-medium">Platform</th>
              <th className="px-4 py-2 font-medium text-right">Net</th>
              <th className="px-4 py-2 font-medium">Bank Match</th>
              <th className="px-4 py-2 font-medium text-right">Δ</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {year.ach_matches.map((m, idx) => (
              <tr
                key={`${m.ach_date}-${m.ach_platform}-${m.ach_net}-${idx}`}
                className="border-b last:border-b-0"
                style={{ borderColor: 'var(--color-border-faint)' }}
                data-testid={`ach-row-${year.year}-${idx}`}
              >
                <td className="px-4 py-2 font-mono text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                  {m.ach_date}
                </td>
                <td className="px-4 py-2">{m.ach_platform}</td>
                <td className="px-4 py-2 text-right">
                  <FinancialValue value={m.ach_net} size="sm" />
                </td>
                <td className="px-4 py-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                  {m.matched_bank_date ? (
                    <>
                      <div className="font-mono">{m.matched_bank_date}</div>
                      <div className="truncate max-w-[18ch]" style={{ color: 'var(--color-text-dim)' }}>
                        {m.matched_bank_description || '—'}
                      </div>
                    </>
                  ) : (
                    <span style={{ color: 'var(--color-text-dim)' }}>—</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  <FinancialValue value={m.amount_delta ?? 0} size="sm" />
                </td>
                <td className="px-4 py-2">
                  {m.is_match ? (
                    <span className="text-xs" style={{ color: 'var(--color-verified)' }}>matched</span>
                  ) : (
                    <span className="text-xs" style={{ color: 'var(--color-needs-human-text)' }}>unmatched</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="flex items-center gap-1 justify-end">
                    {m.is_match ? (
                      <button
                        onClick={() => onAction('unmatch', m)}
                        className="p-1"
                        style={{ color: 'var(--color-text-dim)' }}
                        title="Unmatch"
                        data-testid={`unmatch-${year.year}-${idx}`}
                      >
                        <XCircle size={14} />
                      </button>
                    ) : (
                      <button
                        onClick={() => onAction('rematch', m)}
                        className="p-1"
                        style={{ color: 'var(--color-text-dim)' }}
                        title="Rematch"
                        data-testid={`rematch-${year.year}-${idx}`}
                      >
                        <RefreshCw size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => onAction('correct_amount', m)}
                      className="p-1"
                      style={{ color: 'var(--color-text-dim)' }}
                      title="Correct amount"
                      data-testid={`correct-${year.year}-${idx}`}
                    >
                      <DollarSign size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Override history (full, per default #5)
// ---------------------------------------------------------------------------

function OverrideHistory({ overrides }: { overrides: OverrideRecord[] }) {
  if (!overrides.length) {
    return (
      <div className="border border-dashed rounded-lg p-4 text-xs" style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-dim)' }}>
        No overrides applied. The accountant-review audit trail is empty.
      </div>
    );
  }

  return (
    <div className="border rounded-lg" style={{ borderColor: 'var(--color-border)' }}>
      <div className="px-4 py-2 border-b flex items-center gap-2" style={{ borderColor: 'var(--color-border)' }}>
        <Clock size={14} style={{ color: 'var(--color-text-dim)' }} />
        <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          Override History
        </h3>
        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
          {overrides.length} record{overrides.length === 1 ? '' : 's'}
        </span>
      </div>
      <ul className="divide-y divide-[color:var(--color-border-faint)]">
        {overrides.map((o) => (
          <li
            key={o.override_id}
            className="px-4 py-2 text-xs"
            data-testid={`override-history-${o.override_id}`}
          >
            <div className="flex items-center justify-between">
              <span className="font-mono" style={{ color: 'var(--color-text-secondary)' }}>{o.action}</span>
              <span style={{ color: 'var(--color-text-dim)' }}>
                {new Date(o.created_at).toLocaleString()}
              </span>
            </div>
            <div className="mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
              {describeTarget(o)} — {o.author_name}
            </div>
            {o.reason && (
              <div className="mt-0.5 italic" style={{ color: 'var(--color-text-secondary)' }}>"{o.reason}"</div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function describeTarget(o: OverrideRecord): string {
  const t = o.target || {};
  if (o.action === 'skip_platform') {
    return `platform=${t.platform}`;
  }
  return `${t.year} ${t.ach_platform} ${t.ach_date} $${t.ach_net}`;
}

// ---------------------------------------------------------------------------
// Override modal — one form per action
// ---------------------------------------------------------------------------

interface ModalProps {
  state: {
    kind: ActionKind;
    match?: AchMatch;
    year?: number;
    platform?: string;
  };
  submitting: boolean;
  onSubmit: (payload: {
    action: ActionKind;
    target: Record<string, any>;
    new_value: Record<string, any>;
    reason: string;
  }) => void | Promise<void>;
  onCancel: () => void;
}

function OverrideModal({ state, submitting, onSubmit, onCancel }: ModalProps) {
  const [reason, setReason] = useState('');
  const [bankDate, setBankDate] = useState('');
  const [bankAmount, setBankAmount] = useState('');
  const [bankDesc, setBankDesc] = useState('');

  const titles: Record<ActionKind, string> = {
    unmatch: 'Unmatch ACH payout',
    rematch: 'Rematch ACH payout',
    correct_amount: 'Correct matched amount',
    skip_platform: `Skip platform: ${state.platform}`,
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!reason.trim()) return;

    let target: Record<string, any> = {};
    let new_value: Record<string, any> = {};

    if (state.kind === 'skip_platform') {
      target = { platform: state.platform };
    } else if (state.match && state.year !== undefined) {
      target = {
        year: state.year,
        ach_date: state.match.ach_date,
        ach_platform: state.match.ach_platform,
        ach_net: state.match.ach_net,
      };
      if (state.kind === 'rematch') {
        new_value = {
          matched_bank_date: bankDate,
          matched_bank_amount: parseFloat(bankAmount) || 0,
          matched_bank_description: bankDesc,
        };
      } else if (state.kind === 'correct_amount') {
        new_value = { matched_bank_amount: parseFloat(bankAmount) || 0 };
      }
    }

    await onSubmit({
      action: state.kind,
      target,
      new_value,
      reason: reason.trim(),
    });
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      data-testid="override-modal"
    >
      <form
        onSubmit={handleSubmit}
        className="rounded-lg shadow-xl w-full max-w-md p-5 space-y-4"
        style={{ backgroundColor: 'var(--color-bg-surface)' }}
      >
        <h3 className="text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          {titles[state.kind]}
        </h3>

        {state.match && (
          <div className="text-xs font-mono" style={{ color: 'var(--color-text-muted)' }}>
            {state.match.ach_date} · {state.match.ach_platform} · $
            {state.match.ach_net.toFixed(2)}
          </div>
        )}

        {state.kind === 'rematch' && (
          <>
            <LabeledInput
              label="New bank date"
              type="date"
              value={bankDate}
              onChange={setBankDate}
              testid="input-bank-date"
            />
            <LabeledInput
              label="New bank amount"
              type="number"
              step="0.01"
              value={bankAmount}
              onChange={setBankAmount}
              testid="input-bank-amount"
            />
            <LabeledInput
              label="Bank description"
              value={bankDesc}
              onChange={setBankDesc}
              testid="input-bank-desc"
            />
          </>
        )}

        {state.kind === 'correct_amount' && (
          <LabeledInput
            label="Corrected bank amount"
            type="number"
            step="0.01"
            value={bankAmount}
            onChange={setBankAmount}
            testid="input-bank-amount"
          />
        )}

        <LabeledInput
          label="Reason (audit trail)"
          value={reason}
          onChange={setReason}
          required
          testid="input-reason"
        />

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm rounded"
            style={{ color: 'var(--color-text-secondary)' }}
            data-testid="modal-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !reason.trim()}
            className="px-4 py-1.5 text-sm font-medium rounded disabled:cursor-not-allowed"
            style={{ backgroundColor: (submitting || !reason.trim()) ? 'var(--color-text-ghost)' : 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            data-testid="modal-submit"
          >
            {submitting ? 'Saving…' : 'Save override'}
          </button>
        </div>
      </form>
    </div>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  type = 'text',
  step,
  required,
  testid,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  step?: string;
  required?: boolean;
  testid?: string;
}) {
  return (
    <label className="block">
      <span className="block text-xs mb-1" style={{ color: 'var(--color-text-secondary)' }}>{label}</span>
      <input
        type={type}
        step={step}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none"
        style={{ borderColor: 'var(--color-border)' }}
      />
    </label>
  );
}
